import type { Schemas } from "@/shared/api/client";

export type Timesheet = Schemas["TimesheetOut"];
export type TimesheetRow = Schemas["TimesheetRowOut"];
export type TimeProject = Schemas["TimeProjectOut"];
export type TimeTask = Schemas["TimeTaskOut"];

/** Where time is logged: a task, or a project itself (task null). */
export interface LogTarget {
  project: TimeProject;
  task: TimeTask | null;
}

/** A row's identity: its project and task (or the project itself). */
export function rowKeyOf(target: LogTarget): string {
  return `${target.project.key}:${target.task?.id ?? ""}`;
}

/** A row the person added with the picker, before any time is typed into it. */
export function emptyRow(target: LogTarget, days: readonly string[]): TimesheetRow {
  return {
    project: target.project,
    task: target.task,
    cells: days.map((date) => ({ date, minutes: 0, entry_count: 0, running: false })),
    minutes: 0,
    can_log: true,
  };
}

/** The sheet's rows plus the ones added in this session that have no time yet, in order. */
export function withPendingRows(sheet: Timesheet, pending: readonly LogTarget[]): TimesheetRow[] {
  const known = new Set(sheet.rows.map(rowKeyOf));
  const days = sheet.days.map((day) => day.date);
  const extra: TimesheetRow[] = [];
  for (const target of pending) {
    const key = rowKeyOf(target);
    if (known.has(key)) continue;
    known.add(key);
    extra.push(emptyRow(target, days));
  }
  return [...sheet.rows, ...extra];
}

/**
 * The sheet with one cell set to `minutes` and every total that depends on it recomputed, as
 * the server will answer; the screen shows it at once (an optimistic update).
 */
export function withCell(
  sheet: Timesheet,
  target: LogTarget,
  date: string,
  minutes: number,
): Timesheet {
  const key = rowKeyOf(target);
  const days = sheet.days.map((day) => day.date);
  const rows = sheet.rows.some((row) => rowKeyOf(row) === key)
    ? sheet.rows
    : [...sheet.rows, emptyRow(target, days)];
  const updated = rows
    .map((row) => {
      if (rowKeyOf(row) !== key) return row;
      const cells = row.cells.map((cell) =>
        cell.date === date ? { ...cell, minutes, entry_count: minutes > 0 ? 1 : 0 } : cell,
      );
      return { ...row, cells, minutes: cells.reduce((sum, cell) => sum + cell.minutes, 0) };
    })
    .filter((row) => row.minutes > 0 || row.cells.some((cell) => cell.running));
  const dayTotals = days.map((day) =>
    updated.reduce(
      (sum, row) => sum + (row.cells.find((cell) => cell.date === day)?.minutes ?? 0),
      0,
    ),
  );
  return {
    ...sheet,
    rows: updated,
    days: sheet.days.map((day, index) => ({ ...day, minutes: dayTotals[index] ?? 0 })),
    total_minutes: dayTotals.reduce((sum, value) => sum + value, 0),
  };
}

/** How far the week is towards its target, from 0 to 1 (more than the target still shows 1). */
export function targetProgress(totalMinutes: number, targetMinutes: number): number {
  if (targetMinutes <= 0) return 0;
  return Math.min(1, Math.max(0, totalMinutes / targetMinutes));
}

export interface ProjectWeek {
  project: TimeProject;
  minutes: number;
}

/** The week's time per project, the most first; projects with no time are left out. */
export function minutesByProject(rows: readonly TimesheetRow[]): ProjectWeek[] {
  const totals = new Map<string, ProjectWeek>();
  for (const row of rows) {
    const current = totals.get(row.project.id) ?? { project: row.project, minutes: 0 };
    totals.set(row.project.id, { ...current, minutes: current.minutes + row.minutes });
  }
  return [...totals.values()]
    .filter((item) => item.minutes > 0)
    .sort((first, second) => second.minutes - first.minutes);
}
